import { defineLocalStore } from '@/utils/localStore'
import { computed, ref } from 'vue'
import { cachedObject, CustomDate } from 'best-modules/utils'
import { email, required, useStore, useVuelidate } from '@/plugins'
import type { Dialog } from '@/dialog'
import { userSendToErm, userSingle, userUpdate } from '@/requests/user'
import { setSnackbar } from 'best-modules/plugins'
import type { UserCardTab, UserCardUser } from '@/utils/types/user'
import type {
  UserCardContext,
  UserCardDialogData,
} from '@/utils/userCardDialog'
import { initUserCardGeneralStore } from '@/dialog/components/user-card/tabs/general/general'
import { initUserCardHistoryStore } from '@/dialog/components/user-card/tabs/history/history'
import { initUserCardNotificationsStore } from '@/dialog/components/user-card/tabs/notifications/notifications'
import { initUserCardPermissionsStore } from '@/dialog/components/user-card/tabs/permissions/permissions'
import { initUserCardVacationsStore } from '@/dialog/components/user-card/tabs/vacations/vacations'

export const {
  useLocalStore: useUserCardStore,
  initLocalStore: initUserCardStore,
} = defineLocalStore(
  'userCard',
  (dialog: Dialog & { dialogData?: UserCardDialogData }) => {
    const store = useStore()
    const activeTab = ref('general')
    const user = cachedObject<UserCardUser>({})
    const placeholderMeta = {
      created_at: '2023-01-25 07:57:00',
      updated_at: '2026-04-22 08:12:00',
    }

    user.$setIgnore(
      'absences',
      'departments',
      'ermId',
      'permissions',
      'created_at',
      'updated_at',
      'roleId',
      'isNotificationEmail',
      'isNotificationTelegram',
      'workSchedule',
      'workFormat',
      'accessLevelType',
      'accessLevelTypeId',
      'accessLevelTypeIds',
      'department',
      'hasTelegramPending',
      'position',
      'post',
      'telegramChatId',
      'telegramConnectedAt'
    )

    const isProfileMode = computed(() => dialog?.dialogData?.mode === 'profile')
    const openContext = computed<UserCardContext>(() => {
      return (
        dialog?.dialogData?.context ||
        (isProfileMode.value ? 'profile' : 'list')
      )
    })
    const isListMode = computed(() => openContext.value === 'list')
    const isAdminMode = computed(() => openContext.value === 'admin')
    const readonly = computed(() => !isAdminMode.value)
    const canEdit = computed(() => isAdminMode.value)
    const canEditNotifications = computed(
      () => isAdminMode.value || isProfileMode.value
    )
    const canManageTelegram = computed(
      () => isAdminMode.value || isProfileMode.value
    )

    if (isListMode.value) {
      activeTab.value = 'general'
    }

    const availableTabs = computed<UserCardTab[]>(() => {
      return [
        { value: 'general', label: 'Загальна інформація', disabled: false },
        {
          value: 'history',
          label: 'Історія призначень',
          disabled: isListMode.value,
        },
        { value: 'vacations', label: 'Відпустки', disabled: isListMode.value },
        {
          value: 'permissions',
          label: 'Права доступу',
          disabled: isListMode.value,
        },
        {
          value: 'notifications',
          label: 'Сповіщення',
          disabled: isListMode.value,
        },
      ]
    })

    const showHistoryTab = computed(() => true)

    const v$ = useVuelidate(
      {
        name: { required },
        surname: { required },
        patronymic: {},
        genderId: { required },
        birthDate: { required },
        inn: { required },
        phone: { required },
        email: { required, email },
        profileDWH: { required },
      },
      user
    )

    const departmentName = computed(() => {
      return (user as any).department?.name || ''
    })

    const createdAt = computed(() =>
      new CustomDate(user.created_at || placeholderMeta.created_at).toString({
        time: true,
      })
    )
    const updatedAt = computed(() =>
      new CustomDate(user.updated_at || placeholderMeta.updated_at).toString({
        time: true,
      })
    )

    const setUser = (obj: UserCardUser) => {
      user.$set({
        ...obj,
        birthDate: obj.birthDate,
        inn: obj.inn,
        profileDWH: obj.profileDWH,
        phone: obj.phone,
        email: obj.email,
        post: obj.post,
        genderId: obj.genderId || 2,
        isActive: typeof obj.isActive === 'boolean' ? obj.isActive : true,
      })
    }

    const refreshUserData = () => store.dispatch('user/getUserData')

    function getUser() {
      return userSingle(Number(dialog?.dialogData?.userId)).then(res => {
        setUser(res)

        const userId = res.id || dialog?.dialogData?.userId
        permissionsStore.loadPermissions(userId)
        notificationsStore.loadNotificationSettings(userId)

        return res
      })
    }

    const core = {
      activeTab,
      availableTabs,
      canEdit,
      canEditNotifications,
      canManageTelegram,
      createdAt,
      departmentName,
      dialog,
      getUser,
      isAdminMode,
      isListMode,
      isProfileMode,
      openContext,
      readonly,
      refreshUserData,
      setUser,
      showHistoryTab,
      updatedAt,
      user,
    }

    const generalStore = initUserCardGeneralStore(core)
    const historyStore = initUserCardHistoryStore(core)
    const notificationsStore = initUserCardNotificationsStore(core)
    const permissionsStore = initUserCardPermissionsStore(core)
    const vacationsStore = initUserCardVacationsStore(core)

    const sendToErm = () => {
      if (!user.id || readonly.value || user.ermId) return

      return userSendToErm(Number(user.id)).then(() => {
        getUser()
        setSnackbar({ text: 'Користувача успішно відправлено в ЕРМ' })
      })
    }

    const submit = () => {
      if (canEdit.value) {
        return Promise.all([
          userUpdate(user.id, user.$object),
          notificationsStore.saveNotificationSettings(),
        ]).then(([res]) => {
          setUser(res)
          generalStore.changePassword.value = false
          refreshUserData()

          return res
        })
      }

      if (canEditNotifications.value) {
        return notificationsStore.saveNotificationSettings()
      }

      if (!canEdit.value) {
        return user.$object
      }
    }

    const disabled = computed(() => {
      if (canEdit.value) {
        return (
          !user.$hasChanges &&
          !notificationsStore.hasNotificationSettingsChanges.value
        )
      }

      if (canEditNotifications.value) {
        return !notificationsStore.hasNotificationSettingsChanges.value
      }

      return true
    })

    getUser()

    return {
      ...core,
      assignmentHistory: historyStore.assignmentHistory,
      disabled,
      permissionGroups: permissionsStore.permissionGroups,
      sendToErm,
      submit,
      vacationRows: vacationsStore.vacationRows,
      vacationStats: vacationsStore.vacationStats,
      v$,
    }
  }
)

export type UserCardStore = ReturnType<typeof useUserCardStore>
